import { zodResolver } from '@hookform/resolvers/zod'
import { CircularProgress, Grid } from '@mui/joy'
import { useGate, useStore } from 'effector-react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { userOptionsStore } from '~/shared/api'
import {
  transactionPaymentTypeEnumOptions,
  transactionBalanceTypeEnumOptions,
  transactionMoveFundsTypeEnumOptions,
} from '~/shared/config/constants'
import {
  TransactionBalanceTypeEnum,
  TransactionMoveFundsTypeEnum,
} from '~/shared/config/enums'
import { getObject } from '~/shared/lib/utils'
import { LinkDecorator } from '~/shared/ui/Buttons'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  FormButtons,
  FormProvider,
  NumericInput,
  TextInput,
  TextareaInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import {
  fetchAggregatorAccountByCarOptionFx,
  fetchCarOptions,
  fetchDriverOptions,
  fetchFlowStatementOptions,
  FormGate,
  formSchema,
  FormValues,
} from './model'
import { BalanceUseFormWatchOptions, useFormWatch } from './useFormWatch'

type FormProps = {
  defaultValues?: Partial<FormValues>
  onSuccess?: (formValues: FormValues) => void
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  isReadOnlyDriver?: boolean
  isReadOnlyCar?: boolean
  formWatchOption?: BalanceUseFormWatchOptions
}

export function Form({
  defaultValues,
  onSuccess,
  isReadOnlyDriver,
  isReadOnlyCar,
  formWatchOption,
  isCreating,
  isSubmitting,
  isLoading,
}: FormProps) {
  const { t } = useTranslation()
  useGate(FormGate)

  const form = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(formSchema),
  })

  const disabled = !isCreating || isSubmitting

  const { watch } = form

  const moveFundsType = watch('moveFundsType')
  const moveFundsTypeId = getObject(moveFundsType)?.id

  const driverOptionId = getObject(defaultValues?.driverOption)?.id
  const carOptionId = getObject(defaultValues?.driverOption)?.carOption?.id

  const isAggregatorAccountLoading = useStore(
    fetchAggregatorAccountByCarOptionFx.pending,
  )

  useFormWatch(form, formWatchOption)

  const transactionBalanceOptions = useMemo(() => {
    const options = isCreating
      ? transactionBalanceTypeEnumOptions.filter(
          ({ id }) => id !== TransactionBalanceTypeEnum.BY_DEPT,
        )
      : transactionBalanceTypeEnumOptions

    return userOptionsStore.depositBalanceEnabled
      ? options
      : options.filter(({ id }) => id !== TransactionBalanceTypeEnum.BY_DEPOSIT)
  }, [isCreating])

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues])

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <Container>
        <ContainerTitle>{t('payment')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={4}>
            <AutocompleteInput
              name='moveFundsType'
              label='form.movement type'
              options={transactionMoveFundsTypeEnumOptions}
              disableClearable
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={4}>
            <NumericInput
              name='amount'
              label='form.amount'
              placeholder='form.enter amount'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={4}>
            <AutocompleteInput
              label='form.payment method'
              name='paymentType'
              options={transactionPaymentTypeEnumOptions}
              disableClearable
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
        </Grid>
      </Container>

      <Container>
        <ContainerTitle>{t('transaction information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={4}>
            <AsyncAutocompleteInput
              label='form.driver'
              placeholder='form.select'
              name='driverOption'
              fetchOptions={fetchDriverOptions}
              readOnly={isReadOnlyDriver || disabled}
              skeletonShow={isLoading}
              endDecorator={
                disabled &&
                driverOptionId && (
                  <LinkDecorator
                    tooltipText={t('button.go to driver card')}
                    to={`/drivers/${driverOptionId}`}
                  />
                )
              }
            />
          </Grid>
          <Grid xs={4}>
            <AsyncAutocompleteInput
              label='form.car'
              name='carOption'
              placeholder='form.select'
              fetchOptions={fetchCarOptions}
              readOnly={isReadOnlyCar || disabled}
              skeletonShow={isLoading}
              endDecorator={
                disabled &&
                carOptionId && (
                  <LinkDecorator
                    tooltipText={t('button.go to car card')}
                    to={`/cars/${carOptionId}`}
                  />
                )
              }
            />
          </Grid>
          <Grid xs={4}>
            <TextInput
              label='form.aggregator account'
              placeholder='form.driver aggregator account'
              name='aggregatorAccount'
              readOnly
              skeletonShow={isLoading}
              endDecorator={
                isAggregatorAccountLoading ? (
                  <CircularProgress size='sm' />
                ) : null
              }
            />
          </Grid>
          <Grid xs={6} container>
            <Grid xs={12}>
              <AsyncAutocompleteInput
                label='form.turnover item'
                name='flowStatementOption'
                placeholder='form.select'
                disableClearable
                fetchOptions={() =>
                  fetchFlowStatementOptions(
                    moveFundsTypeId === TransactionMoveFundsTypeEnum.COMING,
                  )
                }
                queryKey={moveFundsTypeId ? [moveFundsTypeId] : []}
                skeletonShow={isLoading}
                readOnly={disabled}
                canAsyncSearch={false}
              />
            </Grid>
            <Grid xs={12}>
              <AutocompleteInput
                label='form.balance section'
                name='balanceType'
                options={transactionBalanceOptions}
                disableClearable
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
          </Grid>
          <Grid xs={6}>
            <TextareaInput
              label='form.comment'
              name='comment'
              placeholder='form.enter comment'
              minRows={4}
              maxRows={4}
              skeletonShow={isLoading}
              readOnly={disabled}
              sx={{
                height: '138px',
              }}
            />
          </Grid>
        </Grid>
      </Container>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
